import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  MapPin,
  ArrowUp,
  ArrowDown,
  X,
  Route,
  ArrowRight,
  RefreshCw,
} from "lucide-react";
import { Button, ErrorMessage, Field } from "./components.jsx";
import {
  FOUNDER_COLORS,
  fullAddress,
  nearbyStops,
} from "../shared/deliveries.js";
import { api } from "./api.js";
import { RouteRunner } from "./workshop-route.jsx";

function OrderMap({ orders, selected, onToggle, placing, onPlace }) {
  const container = useRef(null),
    map = useRef(null),
    markers = useRef(null),
    tileLayer = useRef(null),
    click = useRef(null);
  const [loaded, setLoaded] = useState(false),
    [failed, setFailed] = useState(false);
  click.current = onPlace;
  useEffect(() => {
    const m = L.map(container.current, {
      scrollWheelZoom: false,
    }).setView([43.51, -112.02], 10);
    map.current = m;
    tileLayer.current = L.tileLayer(
      "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
      {
        maxZoom: 19,
        referrerPolicy: "strict-origin-when-cross-origin",
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      },
    ).addTo(m);
    tileLayer.current.on("loading", () => setLoaded(false));
    tileLayer.current.on("tileload", () => {
      setLoaded(true);
      setFailed(false);
    });
    tileLayer.current.on("tileerror", () => setFailed(true));
    markers.current = L.layerGroup().addTo(m);
    m.on("click", (e) => click.current?.(e.latlng));
    const observer = new ResizeObserver(() => m.invalidateSize());
    observer.observe(container.current);
    return () => {
      observer.disconnect();
      m.remove();
    };
  }, []);
  useEffect(() => {
    markers.current.clearLayers();
    orders
      .filter((o) => o.coordinates)
      .forEach((order) => {
        const number = selected.includes(order.id)
            ? selected.indexOf(order.id) + 1
            : "",
          color = FOUNDER_COLORS[order.assignedTo] || "#737f69";
        const icon = L.divIcon({
          className: `delivery-pin ${selected.includes(order.id) ? "pin-selected" : ""}`,
          html: `<span style="background:${color}"><b>${number || "•"}</b></span>`,
          iconSize: [32, 40],
          iconAnchor: [16, 40],
        });
        const title = document.createElement("span");
        title.textContent = `${order.customer.name} · ${order.customer.address}${order.activeRouteId ? " · On a route" : ""}`;
        L.marker([order.coordinates.lat, order.coordinates.lng], { icon })
          .bindTooltip(title)
          .on("click", () => {
            if (!order.activeRouteId) onToggle(order.id);
          })
          .addTo(markers.current);
      });
  }, [orders, selected]);
  useEffect(() => {
    const pinned = orders.filter((o) => o.coordinates);
    if (pinned.length)
      map.current.fitBounds(
        pinned.map((o) => [o.coordinates.lat, o.coordinates.lng]),
        { padding: [45, 45], maxZoom: 14 },
      );
  }, [orders]);
  return (
    <div className={`map-shell ${placing ? "placing-pin" : ""}`}>
      <div
        className="delivery-map"
        ref={container}
        aria-label="Ready delivery map"
      />
      {!loaded && (
        <div className="map-loading">
          {failed ? "Map tiles couldn’t load." : "Loading the local map…"}
          {failed && (
            <button
              className="text-link"
              onClick={() => {
                setFailed(false);
                tileLayer.current.redraw();
              }}
            >
              Retry map
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function DeliveryMap({
  orders,
  admins,
  admin,
  routes,
  onSelect,
  onUpdate,
  onLocate,
  onChanged,
}) {
  const [selected, setSelected] = useState([]),
    [placing, setPlacing] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [name, setName] = useState("Christmas doorstep route"),
    [owner, setOwner] = useState(admin.id),
    [deliveryDate, setDeliveryDate] = useState(""),
    [deliveryWindow, setDeliveryWindow] = useState(""),
    [notify, setNotify] = useState(true),
    [activeId, setActiveId] = useState(null);
  useEffect(() => {
    setSelected((ids) =>
      ids.filter((id) => orders.some((o) => o.id === id && !o.activeRouteId)),
    );
  }, [orders]);
  const active = routes.find((r) => r.id === activeId);
  if (active)
    return (
      <RouteRunner
        key={active.id}
        route={active}
        admin={admin}
        admins={admins}
        onChanged={onChanged}
        onBack={() => setActiveId(null)}
      />
    );
  const chosen = selected
    .map((id) => orders.find((o) => o.id === id))
    .filter(Boolean);
  const toggle = (id) =>
    setSelected((ids) =>
      ids.includes(id) ? ids.filter((i) => i !== id) : [...ids, id],
    );
  function move(index, direction) {
    setSelected((ids) => {
      const next = [...ids];
      [next[index], next[index + direction]] = [
        next[index + direction],
        next[index],
      ];
      return next;
    });
  }
  async function place(coords) {
    if (!placing) return;
    setBusy(true);
    setError("");
    try {
      await onUpdate(
        orders.find((o) => o.id === placing),
        {
          action: "coordinates",
          coordinates: { lat: coords.lat, lng: coords.lng },
        },
      );
      setPlacing(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  async function create(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await api("/api/admin/routes", {
        method: "POST",
        body: {
          name,
          assignedTo: owner,
          orderIds: selected,
          deliveryDate,
          deliveryWindow,
          notify: Boolean(deliveryDate) && notify,
        },
      });
      setSelected([]);
      await onChanged();
      setActiveId(r.route.id);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <div className="delivery-heading">
        <div>
          <strong>{orders.length} orders ready for delivery</strong>
          <span>Click pins or families to choose your stops, in order.</span>
        </div>
        <div className="map-legend">
          {admins.map((a) => (
            <span key={a.id}>
              <i style={{ background: FOUNDER_COLORS[a.id] }} />
              {a.name.split(" ")[0]}
            </span>
          ))}
        </div>
      </div>
      <ErrorMessage>{error}</ErrorMessage>
      {routes.length > 0 && (
        <div className="route-shelf">
          {routes.map((r) => (
            <button
              className="saved-route"
              key={r.id}
              onClick={() => setActiveId(r.id)}
            >
              <Route size={20} />
              <span>
                <strong>{r.name}</strong>
                <small>
                  {
                    admins
                      .find((a) => a.id === r.assignedTo)
                      ?.name.split(" ")[0]
                  }{" "}
                  {r.deliveryDate &&
                    ` · ${new Date(`${r.deliveryDate}T12:00:00Z`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" })}`}{" "}
                  · {r.stops.filter((s) => s.status === "completed").length}/
                  {r.stops.length} delivered ·{" "}
                  {r.status === "active" ? "Resume route" : "Finished"}
                </small>
              </span>
              <ArrowRight size={17} />
            </button>
          ))}
        </div>
      )}
      {placing && (
        <div className="inline-notice">
          Click the map to place this family’s pin.{" "}
          <button className="text-link" onClick={() => setPlacing(null)}>
            Cancel
          </button>
        </div>
      )}
      <div className="route-planning-layout">
        <OrderMap
          orders={orders}
          selected={selected}
          onToggle={toggle}
          placing={placing}
          onPlace={place}
        />
        <div className="route-builder">
          <h2>Make a delivery route.</h2>
          <p>Select families below or on the map. You choose the stop order.</p>
          <div className="route-selected">
            {chosen.map((o, i) => (
              <div key={o.id}>
                <span className="stop-number">{i + 1}</span>
                <span>
                  <strong>{o.customer.name}</strong>
                  <small>{o.customer.city}</small>
                </span>
                <button
                  className="icon-button"
                  aria-label={`Move ${o.customer.name} earlier`}
                  disabled={i === 0}
                  onClick={() => move(i, -1)}
                >
                  <ArrowUp size={13} />
                </button>
                <button
                  className="icon-button"
                  aria-label={`Move ${o.customer.name} later`}
                  disabled={i === chosen.length - 1}
                  onClick={() => move(i, 1)}
                >
                  <ArrowDown size={13} />
                </button>
                <button
                  className="icon-button"
                  aria-label={`Remove ${o.customer.name} from route`}
                  onClick={() => toggle(o.id)}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
          {chosen.length > 1 && (
            <button
              className="text-link"
              onClick={() => setSelected(nearbyStops(chosen).map((o) => o.id))}
            >
              Suggest nearby stops
            </button>
          )}
          <form onSubmit={create}>
            <Field label="Route name">
              <input
                required
                maxLength={80}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
            <Field label="Who’s delivering?">
              <select value={owner} onChange={(e) => setOwner(e.target.value)}>
                {admins.map((a) => (
                  <option value={a.id} key={a.id}>
                    {a.name.split(" ")[0]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Delivery day">
              <input
                type="date"
                min="2026-12-01"
                max="2026-12-31"
                value={deliveryDate}
                onChange={(e) => setDeliveryDate(e.target.value)}
              />
            </Field>
            <Field label="Arrival window (optional)">
              <input
                maxLength={60}
                placeholder="5–8 pm"
                value={deliveryWindow}
                onChange={(e) => setDeliveryWindow(e.target.value)}
              />
            </Field>
            <label className="terms route-notify">
              <input
                type="checkbox"
                checked={Boolean(deliveryDate) && notify}
                disabled={!deliveryDate}
                onChange={(e) => setNotify(e.target.checked)}
              />
              <span>
                {deliveryDate
                  ? `Email ${chosen.length === 1 ? "this family" : chosen.length ? `these ${chosen.length} families` : "each family"} their delivery day`
                  : "Pick a delivery day to email families when it’s coming"}
              </span>
            </label>
            <Button className="full-width" disabled={busy || !chosen.length}>
              <Route size={16} />
              {busy ? "Saving route…" : "Create delivery route"}
            </Button>
          </form>
          <p className="fine-print">
            Selected orders are assigned to this route’s driver. Nearby
            suggestions use map distance; review the order before driving.
          </p>
        </div>
      </div>
      <div className="ready-family-list">
        {orders.map((o) => (
          <article
            key={o.id}
            className={selected.includes(o.id) ? "chosen" : ""}
          >
            <button
              className="ready-family-toggle"
              disabled={Boolean(o.activeRouteId)}
              onClick={() => toggle(o.id)}
            >
              <span className="selection-ring">
                {selected.includes(o.id) ? selected.indexOf(o.id) + 1 : "+"}
              </span>
              <span>
                <strong>{o.customer.name}</strong>
                <small>{fullAddress(o)}</small>
              </span>
            </button>
            <div className="ready-family-controls">
              <label className="delivery-owner">
                <i style={{ background: FOUNDER_COLORS[o.assignedTo] }} />
                <select
                  aria-label={`Delivery owner for ${o.number}`}
                  value={o.assignedTo || admin.id}
                  disabled={busy || Boolean(o.activeRouteId)}
                  onChange={async (e) => {
                    setBusy(true);
                    setError("");
                    try {
                      await onUpdate(o, {
                        action: "transfer",
                        assignedTo: e.target.value,
                      });
                    } catch (e) {
                      setError(e.message);
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  {admins.map((a) => (
                    <option value={a.id} key={a.id}>
                      {a.name.split(" ")[0]}
                    </option>
                  ))}
                </select>
              </label>
              {o.activeRouteId ? (
                <span className="badge badge-ready">On a saved route</span>
              ) : (
                <>
                  <button
                    className="text-link"
                    disabled={busy}
                    onClick={async () => {
                      setBusy(true);
                      setError("");
                      try {
                        await onLocate(o);
                      } catch (e) {
                        setError(e.message);
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    {o.coordinates ? "Refresh pin" : "Find address pin"}
                  </button>
                  <button
                    className="text-link"
                    disabled={busy}
                    onClick={() => setPlacing(o.id)}
                  >
                    {o.coordinates ? "Move pin" : "Place pin"}
                  </button>
                </>
              )}
              <button className="text-link" onClick={() => onSelect(o.id)}>
                Details
              </button>
            </div>
          </article>
        ))}
      </div>
      {!orders.length && (
        <div className="empty-state">
          <MapPin size={38} />
          <h3>Ready when you are.</h3>
          <p>Finished packages appear here after the Build walkthrough.</p>
        </div>
      )}
    </>
  );
}
